'use es6';

import { fromJS, Map as ImmutableMap } from 'immutable';
import I18n from 'I18n';
import { getProperties as getDealProperties } from 'reporting-data/properties/data-type/deals';
import { DEALS } from 'reporting-data/constants/dataTypes';
import { get as getPipelines } from 'reporting-data/retrieve/inboundDb/pipelines';
import { getCurrencyCode, getPipelineReferences, getDealStageReferences, getPropertyReferences, getProgressReferences, getPushStatusReferences, getSnapshotDateReferences, getDealbreakdownReferences, getDealChangeTypeReferences } from '../salesExplorer';
import { isSearch } from './helpers';
import { getSummaryProperties } from './resolveMetricProperties';
import memoize from 'transmute/memoize';
const getDealStageReferencesFromProperties = memoize(key => {
  return getDealProperties().then(properties => properties.get(key)).then(dealStageProperties => getDealStageReferences(dealStageProperties));
});
const getProperties = ({
  report,
  response
}) => Promise.all([getDealProperties(), getPipelines(DEALS), getCurrencyCode(), getDealStageReferencesFromProperties('dealstage')]).then(([properties, pipelines, currencyCode, dealStageReferences]) => fromJS({
  hubspot_owner_id: {
    label: properties.getIn(['hubspot_owner_id', 'label'])
  },
  hubspot_team_id: {
    label: properties.getIn(['hubspot_team_id', 'label'])
  },
  periodDate: {
    label: properties.getIn(['closedate', 'label']),
    type: 'date',
    format: {
      frequency: report.getIn(['config', 'frequency'])
    }
  },
  closedInPeriodAmount: {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.closedInPeriodAmount.default'),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  quotaAmount: {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.quotaAmount.default'),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  accumulatedQuota: {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.accumulatedQuota.default'),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  forecastAmount: {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.forecastAmount.default'),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  closedInPeriodCount: {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.closedInPeriodCount.default'),
    type: 'number'
  },
  attainmentPercentage: {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.attainmentPercentage.default'),
    type: 'percent'
  },
  gapToQuota: {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.gapToQuota.default'),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  dealname: {
    label: properties.getIn(['dealname', 'label'])
  },
  dealstage: {
    label: properties.getIn(['dealstage', 'label']),
    references: dealStageReferences
  },
  pipeline: {
    label: properties.getIn(['pipeline', 'label']),
    references: getPipelineReferences(pipelines)
  },
  closedate: {
    label: properties.getIn(['closedate', 'label']),
    type: 'date',
    format: {
      frequency: report.getIn(['config', 'frequency'])
    }
  },
  createdate: {
    label: properties.getIn(['createdate', 'label']),
    type: 'date',
    format: {
      frequency: report.getIn(['config', 'frequency'])
    }
  },
  amount_in_home_currency: {
    label: properties.getIn(['amount_in_home_currency', 'label']),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  amountInHomeCurrency: {
    label: properties.getIn(['amountInHomeCurrency', 'label']),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  hs_manual_forecast_category: {
    label: properties.getIn(['hs_manual_forecast_category', 'label']),
    references: getPropertyReferences(properties.get('hs_manual_forecast_category', ImmutableMap()))
  },
  hs_is_closed: {
    label: properties.getIn(['hs_is_closed', 'label']),
    type: 'boolean'
  },
  'SUM|projectedAmountInHomeCurrency': {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.forecastAmount.total'),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  'SUM|hs_projected_amount_in_home_currency': {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.forecastAmount.total'),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  'SUM|count': {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.count'),
    type: 'number'
  },
  'SUM|amount_in_home_currency': {
    label: properties.getIn(['amount_in_home_currency', 'label']),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  'SUM|amountInHomeCurrency': {
    label: properties.getIn(['amountInHomeCurrency', 'label']),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  'SUM|closedAmountInHomeCurrency': {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.closedAmountInHomeCurrency'),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  'SUM|hs_closed_amount_in_home_currency': {
    label: I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.closedAmountInHomeCurrency'),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  hs_analytics_source: {
    label: properties.getIn(['hs_analytics_source', 'label']),
    references: getPropertyReferences(properties.get('hs_analytics_source', ImmutableMap()))
  },
  hs_close_date_progress: {
    references: getProgressReferences()
  },
  hs_deal_stage_progress: {
    references: getProgressReferences()
  },
  hs_manual_category_progress: {
    references: getProgressReferences()
  },
  hs_amount_change_event: {
    references: getProgressReferences()
  },
  hs_amount_change_percentage: {
    type: 'percent'
  },
  hs_created_in_period: {
    references: {
      true: {
        label: I18n.text('reporting-ui-components.customWidgets.changeHistoryDealList.propertyChange.created')
      }
    }
  },
  initialAmountInHomeCurrency: {
    type: 'currency',
    label: I18n.text('reporting-ui-components.customWidgets.waterfall.properties.initialAmountInHomeCurrency'),
    format: {
      currencyCode
    }
  },
  finalAmountInHomeCurrency: {
    type: 'currency',
    label: I18n.text('reporting-ui-components.customWidgets.waterfall.properties.finalAmountInHomeCurrency'),
    format: {
      currencyCode
    }
  },
  amountInHomeCurrencyChange: {
    type: 'currency',
    label: I18n.text('reporting-ui-components.customWidgets.waterfall.properties.amountInHomeCurrencyChange'),
    format: {
      currencyCode
    }
  },
  initialCloseDate: {
    type: 'datetime',
    label: I18n.text('reporting-ui-components.customWidgets.waterfall.properties.initialCloseDate')
  },
  finalCloseDate: {
    type: 'datetime',
    label: I18n.text('reporting-ui-components.customWidgets.waterfall.properties.finalCloseDate')
  },
  closedWonReason: {
    type: 'string',
    label: I18n.text('reporting-ui-components.customWidgets.waterfall.properties.closedWonReason')
  },
  closedLostReason: {
    type: 'string',
    label: I18n.text('reporting-ui-components.customWidgets.waterfall.properties.closedLostReason')
  },
  finalPipelineStage: {
    type: 'enumeration',
    label: I18n.text('reporting-ui-components.customWidgets.waterfall.properties.finalPipelineStage'),
    references: dealStageReferences
  },
  numberOfPushes: {
    type: 'number',
    label: I18n.text('reporting-ui-components.customWidgets.dealPushAnalysis.drilldownProperties.pushCount')
  },
  mostRecentPushInitialCloseDate: {
    type: 'datetime',
    label: I18n.text('reporting-ui-components.customWidgets.dealPushAnalysis.drilldownProperties.mostRecentPushInitialCloseDate')
  },
  mostRecentPushFinalCloseDate: {
    type: 'datetime',
    label: I18n.text('reporting-ui-components.customWidgets.dealPushAnalysis.drilldownProperties.mostRecentPushFinalCloseDate')
  },
  mostRecentPushStageId: {
    type: 'enumeration',
    label: I18n.text('reporting-ui-components.customWidgets.dealPushAnalysis.drilldownProperties.mostRecentPushStageId'),
    references: dealStageReferences
  },
  snapshot_period_closedate: {
    label: I18n.text('reporting-ui-components.customWidgets.pipelineSnapshots.properties.snapshot_period_closedate'),
    type: 'date'
  },
  snapshot_period_dealstage: {
    label: I18n.text('reporting-ui-components.customWidgets.pipelineSnapshots.properties.snapshot_period_dealstage'),
    references: dealStageReferences
  },
  snapshot_period_amount_in_home_currency: {
    label: I18n.text('reporting-ui-components.customWidgets.pipelineSnapshots.properties.snapshot_period_amount_in_home_currency'),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  pushStatus: {
    label: I18n.text('reporting-ui-components.customWidgets.dealPushAnalysis.options.label.pushstatus'),
    name: 'pushStatus',
    type: 'enumeration',
    references: getPushStatusReferences()
  },
  PUSHED: {
    label: I18n.text('reporting-ui-components.customWidgets.dealPushAnalysis.options.label.dealsPushed')
  },
  NOT_PUSHED: {
    label: I18n.text('reporting-ui-components.customWidgets.dealPushAnalysis.options.label.dealsNotPushed')
  },
  snapshotDate: {
    label: I18n.text('reporting-ui-components.customWidgets.pipelineSnapshots.snapshotDate'),
    type: 'date',
    references: getSnapshotDateReferences(response)
  },
  breakdownMin: {
    type: 'currency',
    format: {
      currencyCode
    }
  },
  breakdownMax: {
    type: 'currency',
    format: {
      currencyCode
    }
  },
  dealBreakdown: {
    label: I18n.text(`reporting-ui-components.customWidgets.waterfall.dealBreakdown.label`),
    references: getDealbreakdownReferences(report)
  },
  count: {
    type: 'number',
    label: I18n.text(`reporting-ui-components.customWidgets.waterfall.properties.count`)
  },
  netPipelineImpact: {
    label: I18n.text(`reporting-ui-components.customWidgets.waterfall.properties.netPipelineImpact`),
    type: 'currency',
    format: {
      currencyCode
    }
  },
  deal_change_type: {
    label: I18n.text(`reporting-ui-components.customWidgets.changeHistoryDealList.deal_change_type.label`),
    references: getDealChangeTypeReferences()
  }
}));
export default ((props = []) => ({
  report,
  response,
  datasets
}) => {
  return getProperties({
    report,
    response,
    datasets
  }).then(properties => {
    const isSearchReport = isSearch(report);
    return {
      report,
      response,
      datasets: datasets.map(dataset => dataset.set('properties', properties.filter((_, prop) => props.includes(prop) || props.length === 0)
      // in a search report, a date property should always use the default date format, and thus the format prop should be deleted
      .map(property => isSearchReport && property.get('type') === 'date' ? property.delete('format') : property)))
    };
  }).then(datasetObject => getSummaryProperties(datasetObject));
});